import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class VersionCheckService implements OnDestroy {
    // Check for updates every 5 minutes (300000ms)
    private readonly CHECK_INTERVAL = 300000;

    // Current version from package.json
    private currentVersion: string = '1.6.10';

    // Observable to notify components about new updates
    private updateAvailableSubject = new BehaviorSubject<boolean>(false);
    public updateAvailable$ = this.updateAvailableSubject.asObservable();

    private checkSubscription?: Subscription;

    constructor(private http: HttpClient) { }

    /**
     * Initialize version checking
     * Starts periodic polling to check for new versions
     */
    initVersionCheck(): void {
        // Perform initial check immediately
        this.checkVersion();

        // Then check periodically
        this.checkSubscription = interval(this.CHECK_INTERVAL)
            .pipe(
                switchMap(() => this.fetchRemoteVersion()),
                catchError(error => {
                    console.warn('Version check failed:', error);
                    return of(null);
                })
            )
            .subscribe(remoteVersion => {
                if (remoteVersion && this.isNewerVersion(remoteVersion)) {
                    this.updateAvailableSubject.next(true);
                }
            });
    }

    /**
     * Manually trigger a version check
     */
    checkVersion(): void {
        this.fetchRemoteVersion()
            .pipe(
                catchError(error => {
                    console.warn('Version check failed:', error);
                    return of(null);
                })
            )
            .subscribe(remoteVersion => {
                if (remoteVersion && this.isNewerVersion(remoteVersion)) {
                    this.updateAvailableSubject.next(true);
                }
            });
    }

    /**
     * Fetch the version from the server
     * Adds timestamp to prevent caching
     */
    private fetchRemoteVersion() {
        const timestamp = new Date().getTime();
        return this.http.get<{ version: string }>(`/assets/version.json?t=${timestamp}`);
    }

    /**
     * Compare versions to determine if remote is newer
     * @param remoteVersion Version string from server
     * @returns true if remote version is newer
     */
    private isNewerVersion(remoteVersionObj: { version: string }): boolean {
        const remoteVersion = remoteVersionObj.version;

        // Parse versions (e.g., "1.4.0" -> [1, 4, 0])
        const currentParts = this.currentVersion.split('.').map(Number);
        const remoteParts = remoteVersion.split('.').map(Number);

        // Compare each part
        for (let i = 0; i < Math.max(currentParts.length, remoteParts.length); i++) {
            const current = currentParts[i] || 0;
            const remote = remoteParts[i] || 0;

            if (remote > current) {
                return true;
            } else if (remote < current) {
                return false;
            }
        }

        return false;
    }

    /**
     * Perform a hard refresh of the application
     * Clears cache and reloads the page
     */
    reloadApp(): void {
        // Clear browser cache and reload
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
            });
        }

        // Hard reload the page (bypass cache)
        window.location.reload();
    }

    /**
     * Stop version checking
     */
    ngOnDestroy(): void {
        if (this.checkSubscription) {
            this.checkSubscription.unsubscribe();
        }
    }
}




















